/**
 * Pure decision logic behind `MergeMemberModal`.
 *
 * Lives apart from the component for one reason: `merge_member` takes two ids
 * that look interchangeable and are not — one of the two profiles is closed
 * and cannot be recovered. Getting the direction backwards is the single worst
 * thing this control can do, and the SPA tree carries no component tests (it
 * is ESLint-ignored and coverage-excluded), so the part worth proving is
 * pulled out to where it can be proven.
 */

/** Money/schedule rows on the profile the modal was opened from. */
export interface MergeMemberCounts {
  readonly bookings: number;
  readonly payments: number;
  readonly packages: number;
  readonly memberships: number;
}

/** The wire shape `merge_member` expects: `from` is absorbed, `to` survives. */
export interface MergeDirection {
  readonly from_member_id: string;
  readonly to_member_id: string;
}

/**
 * Map "which profile do we keep" onto the action's absorb/survive pair.
 *
 * `keepThis === false` is the default and the common case: staff open a
 * placeholder minted by a first Instagram DM or WhatsApp message and fold it
 * into the real account, so THIS profile is the one absorbed.
 */
export function mergeDirection(args: {
  thisMemberId: string;
  otherMemberId: string;
  keepThis: boolean;
}): MergeDirection {
  return args.keepThis
    ? { from_member_id: args.otherMemberId, to_member_id: args.thisMemberId }
    : { from_member_id: args.thisMemberId, to_member_id: args.otherMemberId };
}

/**
 * "2 bookings, 1 payment and 3 packages" — zero rows are omitted so the
 * sentence only ever mentions things that actually exist. Empty string when
 * there is nothing to move, which is the case the caller wants to stay silent
 * about (no warning is better than "this profile has nothing").
 */
export function describeMergeCounts(counts: MergeMemberCounts): string {
  const parts: string[] = [];
  const push = (n: number, one: string, many: string): void => {
    if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  push(counts.bookings, "booking", "bookings");
  push(counts.payments, "payment", "payments");
  push(counts.packages, "package", "packages");
  push(counts.memberships, "membership", "memberships");
  if (parts.length === 0) return "";
  const last = parts[parts.length - 1] as string;
  if (parts.length === 1) return last;
  return `${parts.slice(0, -1).join(", ")} and ${last}`;
}

/** Row counts returned by a successful `merge_member` dispatch. */
export interface MergeMovedCounts {
  readonly bookings_moved?: number;
  readonly payments_moved?: number;
  readonly packages_moved?: number;
  readonly memberships_moved?: number;
  readonly flows_moved?: number;
  /** Identity metadata keys the survivor inherited, e.g. `instagram_handle`. */
  readonly identity_keys_moved?: ReadonlyArray<string>;
  /**
   * `members` identity COLUMNS the survivor inherited — `phone` / `email` /
   * `display_name`. Reported separately from the metadata keys because they are
   * what staff actually check afterwards, and their silent loss is the defect
   * this whole change exists to close.
   */
  readonly identity_columns_moved?: ReadonlyArray<string>;
  /** Columns deliberately refused, with a stable reason code. */
  readonly identity_columns_skipped?: ReadonlyArray<{
    readonly column: string;
    readonly reason: string;
  }>;
}

/** `instagram_handle` → "Instagram handle"; unknown keys degrade readably. */
function identityKeyLabel(key: string): string {
  if (key === "instagram_handle") return "Instagram handle";
  return key.replace(/_/g, " ");
}

/** `display_name` → "Name". Used by both the preview table and the result line. */
export function identityColumnLabel(column: string): string {
  if (column === "display_name") return "Name";
  if (column === "phone") return "Phone";
  if (column === "email") return "Email";
  return column.replace(/_/g, " ");
}

/**
 * The success line. Reports what the ACTION said it moved rather than what the
 * page predicted, so a merge that touched more than the page knew about (a row
 * created between load and confirm) is reported honestly.
 */
export function describeMergeResult(
  moved: MergeMovedCounts,
  survivorLabel: string,
): string {
  const rows: ReadonlyArray<readonly [number, string, string]> = [
    [moved.bookings_moved ?? 0, "booking", "bookings"],
    [moved.payments_moved ?? 0, "payment", "payments"],
    [moved.packages_moved ?? 0, "package", "packages"],
    [moved.memberships_moved ?? 0, "membership", "memberships"],
    [moved.flows_moved ?? 0, "flow", "flows"],
  ];
  const detail = rows
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`)
    .join(", ");
  const target = survivorLabel === "" ? "the other profile" : survivorLabel;
  // Identity keys are reported separately from the row counts because they are
  // a different kind of thing: nothing was RE-PARENTED, an identifier was
  // carried across so the survivor can be recognised by it next time. Worth
  // saying out loud — it is the difference between a merge that holds and one
  // whose duplicate walks back in.
  const carried = [
    ...(moved.identity_columns_moved ?? []).map(identityColumnLabel),
    ...(moved.identity_keys_moved ?? []).map(identityKeyLabel),
  ];
  const carriedLine =
    carried.length === 0 ? "" : ` Carried over the ${joinWithAnd(carried)}.`;
  // A refused column is reported too. Silence here is exactly what let the
  // 2026-09-04 phone loss pass as a clean merge.
  const skipped = (moved.identity_columns_skipped ?? []).map(
    (s) =>
      `${identityColumnLabel(s.column).toLowerCase()} (${
        skipReasonNote(s.reason) ?? "not carried"
      })`,
  );
  const skippedLine =
    skipped.length === 0 ? "" : ` Did not carry the ${joinWithAnd(skipped)}.`;
  return detail === ""
    ? `Merged into ${target}. Nothing to move.${carriedLine}${skippedLine}`
    : `Merged into ${target}. Moved ${detail}.${carriedLine}${skippedLine}`;
}

/** "a", "a and b", "a, b and c". */
function joinWithAnd(parts: ReadonlyArray<string>): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1] as string}`;
}

// ---------------------------------------------------------------------------
// Merge PREVIEW — the side-by-side shown BEFORE the irreversible confirm.
// ---------------------------------------------------------------------------

/**
 * The preview exists because the merge's most consequential effect was also its
 * least visible one. Row counts were shown; IDENTITY was not — so a merge that
 * silently dropped a phone number looked exactly like a merge that did not
 * (2026-09-04). Everything below is presentation only: every verdict and every
 * warning code is decided server-side by `preview_member_merge`, which shares
 * its rules with the merge itself. Nothing here re-derives a decision, because a
 * preview that disagrees with the merge is worse than no preview at all.
 */

/** One member's identity, as `preview_member_merge` returns it. */
export interface PreviewProfile {
  readonly member_id: string;
  readonly display_name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly status: string;
  readonly instagram_handle: string | null;
  readonly email_is_placeholder: boolean;
  readonly created_at: string;
}

export interface PreviewIdentityVerdict {
  readonly column: string;
  readonly decision: "carry" | "skip";
  readonly reason: string | null;
}

export interface PreviewMoveCounts {
  readonly bookings: number;
  readonly payments: number;
  readonly packages: number;
  readonly memberships: number;
  readonly flows: number;
  readonly tags: number;
  readonly conversations: number;
  readonly vendor_links: number;
  readonly channel_bindings: number;
  readonly session_notes: number;
  readonly session_feedback: number;
}

export interface MergePreview {
  readonly absorbed: PreviewProfile;
  readonly survivor: PreviewProfile;
  readonly identity: ReadonlyArray<PreviewIdentityVerdict>;
  readonly metadata_keys_carried: ReadonlyArray<string>;
  readonly moves: PreviewMoveCounts;
  readonly warnings: ReadonlyArray<string>;
}

/** A row of the side-by-side identity table. */
export interface PreviewIdentityRow {
  readonly column: string;
  readonly label: string;
  /** The closing profile's value, or null when it has none. */
  readonly absorbedValue: string | null;
  /** The surviving profile's value, or null when it has none. */
  readonly survivorValue: string | null;
  /** What the survivor ends up with once the merge runs. */
  readonly resultValue: string | null;
  /**
   * `moves`   — the survivor gains this value.
   * `keeps`   — the survivor already had one; the absorbed value is discarded.
   * `lost`    — the absorbed profile had a value, the survivor has none, and it
   *             will NOT be carried. This is the state that caused the incident,
   *             and the row the operator most needs to see.
   * `neither` — nobody had a value.
   */
  readonly effect: "moves" | "keeps" | "lost" | "neither";
  /** Short human explanation for a `lost` row; null otherwise. */
  readonly note: string | null;
}

function valueOf(profile: PreviewProfile, column: string): string | null {
  const raw =
    column === "phone"
      ? profile.phone
      : column === "email"
        ? profile.email
        : column === "display_name"
          ? profile.display_name
          : null;
  return raw !== null && raw.trim() !== "" ? raw : null;
}

/** Why a column was refused, in words an operator can act on. */
export function skipReasonNote(reason: string | null): string | null {
  switch (reason) {
    case "placeholder_email":
      return "auto-generated address, not a real mailbox";
    case "phone_in_use_by_other_member":
      return "another member already has this number";
    default:
      return null;
  }
}

/**
 * Build the side-by-side identity rows: closing profile on the left, surviving
 * profile on the right, and what the survivor ends up with.
 *
 * The `effect` is derived from the SERVER's verdict, never re-decided here — a
 * `skip` with a value present on the left and nothing on the right is exactly
 * the silent-loss case, and it is labelled `lost` so the UI can shout about it.
 */
export function buildPreviewIdentityRows(
  preview: MergePreview,
): ReadonlyArray<PreviewIdentityRow> {
  return preview.identity.map((verdict) => {
    const absorbedValue = valueOf(preview.absorbed, verdict.column);
    const survivorValue = valueOf(preview.survivor, verdict.column);
    const carries = verdict.decision === "carry";

    let effect: PreviewIdentityRow["effect"];
    if (carries) {
      effect = "moves";
    } else if (survivorValue !== null) {
      effect = "keeps";
    } else if (absorbedValue !== null) {
      effect = "lost";
    } else {
      effect = "neither";
    }

    return {
      column: verdict.column,
      label: identityColumnLabel(verdict.column),
      absorbedValue,
      survivorValue,
      resultValue: carries ? absorbedValue : survivorValue,
      effect,
      note: effect === "lost" ? skipReasonNote(verdict.reason) : null,
    };
  });
}

/** The non-zero move counts, in the order staff care about, ready to render. */
export function buildPreviewMoveRows(
  moves: PreviewMoveCounts,
): ReadonlyArray<{ readonly label: string; readonly count: number }> {
  const rows: ReadonlyArray<readonly [keyof PreviewMoveCounts, string, string]> =
    [
      ["bookings", "booking", "bookings"],
      ["payments", "payment", "payments"],
      ["packages", "package", "packages"],
      ["memberships", "membership", "memberships"],
      ["flows", "marketing flow", "marketing flows"],
      ["tags", "tag", "tags"],
      ["conversations", "conversation", "conversations"],
      ["vendor_links", "linked account", "linked accounts"],
      ["channel_bindings", "chat number", "chat numbers"],
      ["session_notes", "session note", "session notes"],
      ["session_feedback", "feedback entry", "feedback entries"],
    ];
  return rows.flatMap(([key, one, many]) => {
    const count = moves[key];
    return count > 0 ? [{ label: count === 1 ? one : many, count }] : [];
  });
}

/**
 * Turn a server warning code into a sentence. Unknown codes degrade to null
 * rather than rendering a raw identifier at an operator.
 */
export function previewWarningText(
  code: string,
  absorbedLabel: string,
  survivorLabel: string,
): string | null {
  switch (code) {
    case "absorbed_has_history":
      return `${absorbedLabel} already has money or bookings on it. Merging moves them to ${survivorLabel}; it is easiest to merge before any exist.`;
    case "phone_in_use_by_other_member":
      return "The phone number cannot be carried over — another member already has it. Resolve that duplicate first if the number should follow.";
    case "placeholder_email_not_carried":
      return "The closing profile's email is an auto-generated placeholder, so it will not be carried over. That is deliberate — it is not a real mailbox.";
    case "survivor_left_without_email":
      return `${survivorLabel} will have no email address after this merge.`;
    case "survivor_left_without_phone":
      return `${survivorLabel} will have no phone number after this merge.`;
    case "direction_may_be_inverted":
      return `Check the direction. ${survivorLabel} looks like the auto-created profile and ${absorbedLabel} like the real account — you may want to swap.`;
    default:
      return null;
  }
}

/** True when the preview contains something the operator should read first. */
export function previewHasSeriousWarning(
  preview: MergePreview,
): boolean {
  return (
    preview.warnings.includes("direction_may_be_inverted") ||
    buildPreviewIdentityRows(preview).some((r) => r.effect === "lost")
  );
}
